import { askBackend, type BackendReply, encodeReply, isBackendUrl, NO_CONTENT, replyText, SAFE_METHODS } from "./transport";

const abortError = () => new DOMException("The operation was aborted.", "AbortError");

const resolveReply = (method: string, url: string, body: () => unknown): Promise<BackendReply> | null => {
	if (isBackendUrl(url)) return askBackend(method, url, body());
	if (!SAFE_METHODS.has(method)) return Promise.resolve(NO_CONTENT);
	return null;
};

export const patchFetch = () => {
	const nativeFetch = window.fetch.bind(window);

	window.fetch = async (input, init) => {
		const request = input instanceof Request ? input : null;
		const url = request ? request.url : String(input);
		const method = (init?.method ?? request?.method ?? "GET").toUpperCase();
		const signal = init?.signal ?? request?.signal;
		const body = () => init?.body ?? (request && !SAFE_METHODS.has(method) ? request.clone().body : null);

		const pending = resolveReply(method, url, body);
		if (!pending) return nativeFetch(input, init);
		if (signal?.aborted) throw abortError();
		const reply = await pending;
		if (signal?.aborted) throw abortError();
		return new Response(replyText(reply) || null, { status: reply.status, headers: { "Content-Type": "application/json" } });
	};
};

interface XhrTarget {
	method: string;
	url: string;
}

const complete = (xhr: XMLHttpRequest, reply: BackendReply) => {
	const text = replyText(reply);
	const fields: Record<string, unknown> = {
		readyState: XMLHttpRequest.DONE,
		status: reply.status,
		statusText: "",
		responseURL: "",
		responseText: text,
		response: encodeReply(reply, xhr.responseType),
		getResponseHeader: (name: string) => (name.toLowerCase() === "content-type" ? "application/json" : null),
		getAllResponseHeaders: () => "content-type: application/json\r\n",
	};
	for (const [key, value] of Object.entries(fields)) Object.defineProperty(xhr, key, { configurable: true, value });
	xhr.dispatchEvent(new Event("readystatechange"));
	xhr.dispatchEvent(new ProgressEvent("load"));
	xhr.dispatchEvent(new ProgressEvent("loadend"));
};

export const patchXhr = () => {
	const targets = new WeakMap<XMLHttpRequest, XhrTarget>();
	const { open, send } = XMLHttpRequest.prototype;

	XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
		targets.set(this, { method: method.toUpperCase(), url: String(url) });
		return (open as (...args: unknown[]) => void).call(this, method, url, ...rest);
	} as XMLHttpRequest["open"];

	XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: XMLHttpRequestBodyInit | null) {
		const target = targets.get(this);
		const pending = target && resolveReply(target.method, target.url, () => body);
		if (!pending) return send.call(this, body);
		void pending.then((reply) => complete(this, reply));
	};
};

export const patchBeacon = () => {
	if (typeof navigator.sendBeacon === "function") Object.defineProperty(navigator, "sendBeacon", { configurable: true, value: () => true });
};
